import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const TermsAcceptance = ({ onClose, isInformational = false }) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(isInformational ? 0 : 15);
  const [buttonEnabled, setButtonEnabled] = useState(isInformational);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    let timer;
    if (!isInformational) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setButtonEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      document.body.style.overflow = 'unset';
      if (timer) clearInterval(timer);
    };
  }, [isInformational]);

  const handleAccept = () => {
    localStorage.setItem('termsAccepted', 'true');
    localStorage.setItem('termsAcceptedAt', Date.now().toString());
    if (onClose) onClose();
  };

  const handleDecline = () => {
    window.location.href = 'https://www.educaloi.qc.ca/';
  };

  const handleInformationalClose = () => {
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex justify-center sm:items-center z-50 p-0 sm:p-4 transition-opacity duration-300 overflow-hidden">
      <div className="bg-white shadow-2xl w-full h-full sm:max-h-[85vh] sm:max-w-5xl sm:rounded-lg flex flex-col overflow-hidden border-blue-200">
        {/* Header */}
        <div className="bg-primary text-white py-2.5 px-3 sm:py-3 sm:px-4 sticky top-0 z-10 flex items-center justify-between flex-shrink-0">
          <h3 className="text-base sm:text-lg font-bold flex items-center">
            <InformationCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
            <span>À lire avant de continuer</span>
          </h3>
          <button 
            onClick={isInformational ? handleInformationalClose : handleDecline}
            className="text-white hover:text-blue-100 transition-colors p-1"
            aria-label={isInformational ? t('termsAcceptance.close', 'Fermer') : t('termsAcceptance.decline', 'Refuser et quitter')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
            
        {/* Content area */}
        <div className="p-2.5 pt-8 sm:p-4 sm:pt-5 overflow-y-auto flex-grow">
          <div className="bg-yellow-50 border border-yellow-300 p-3 rounded-lg text-yellow-800 mb-4">
            <p className="flex items-start text-sm sm:text-base">
              <span className="font-semibold mr-1.5 text-xl">⚠️</span>
              <span><strong>Important :</strong> Les informations fournies sont d'ordre général. Pour des décisions importantes, consultez toujours un professionnel du droit.</span>
            </p>
          </div>
              
          {/* Simplified Content */}
          <div className="bg-blue-50 rounded-lg p-3 md:p-4 border-l-4 border-blue-400 shadow-sm h-full mb-4">
            <h4 className="font-bold text-blue-700 text-base flex items-center mb-2">
              <span className="text-2xl mr-2">💡</span>
              Notre Objectif
            </h4>
            <p className="text-sm text-gray-700">
              ClicDroits.info a pour vocation de vous fournir des informations juridiques générales plus détaillées pour vous aider à mieux comprendre le système judiciaire québécois en droit de la famille. 
              Nous visons à vous éclairer sur les différentes options et processus, afin de vous préparer à une éventuelle consultation avec un avocat ou un autre professionnel du droit.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Veuillez noter que ce site <strong>ne fournit pas de conseils juridiques personnalisés</strong> et ne remplace en aucun cas l'avis d'un avocat. Les outils et informations sont là pour vous guider et vous informer, mais chaque situation est unique.
            </p>
          </div>
        </div>
            
        {/* Footer with buttons */}
        <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50 sticky bottom-0 z-10 flex-shrink-0">
          {isInformational ? (
            <div className="flex justify-end">
              <button 
                onClick={handleInformationalClose}
                className="bg-primary hover:bg-primary-dark text-white font-medium py-2.5 px-6 rounded-lg transition-colors w-full sm:w-auto"
              >
                {t('termsAcceptance.close', 'Fermer')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button 
                onClick={handleDecline}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2.5 px-6 rounded-lg transition-colors w-full sm:w-auto"
              >
                {t('termsAcceptance.decline')}
              </button>
              <button 
                onClick={handleAccept}
                disabled={!buttonEnabled}
                className={`font-medium py-2.5 px-6 rounded-lg transition-colors w-full sm:w-auto flex items-center justify-center
                            ${buttonEnabled ? 'bg-primary hover:bg-primary-dark text-white' : 'bg-gray-400 text-gray-100 cursor-not-allowed'}`}
              >
                {buttonEnabled ? (
                  <>
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {t('termsAcceptance.accept')}
                  </>
                ) : `${t('termsAcceptance.accept')} (${countdown}s)`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptance; 